import React, { Suspense, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Security, LoginCallback } from "@okta/okta-react";
import SecureRoute from "./components/SecureRoute";
import { OktaAuth } from "@okta/okta-auth-js";
import {
  oktaAuthConfig,
  oktaSignInConfig,
  appConfig,
  featureFlags,
} from "./config";
import useAuthHandler from "./hooks/AuthHandler";
import { useAnalytics } from "./hooks/Analytics";
import { PageViewEventBase } from "./types/AnalyticsTypes.d";
import {useSafeRedirect} from "./hooks/SafeRedirect";
import { FlagsmithProvider } from "./providers/flagsmithProvider";
import Loading from "./components/loader/Loading";
import { lazyRouteComponents } from "./utils";

const {
  Home,
  Logout,
  Impersonate,
  Refresh,
  SSOStaff,
  SSOUserRedirect,
  Account,
  SelectMember,
  Guest,
  Registration,
  RegistrationComplete,
  SignIn,
  MFAEnroll,
  AccountVerification,
  AccountSession,
  MFASMSEnroll,
  MFAEmailEnroll,
  MFAChallenge,
  AccountUnlocked,
  EmailSent,
  ValidateToken,
  PasswordReset,
  ForgotPassword,
  PasswordResetComplete,
  ValidatedRoute,
  SignOut,
  ConnectAccount,
  ConnectAccountRelation,
  MFAChallengeTotp,
  PasswordUpdate,
  MantineLayout,
  WhyConnectPlan,
  Login,
  AuthenticationCallback,
  RequestError,
} = lazyRouteComponents;

const oktaAuth = new OktaAuth(oktaAuthConfig);

const AppWithRouterAccess = () => {
  const {legacyRedirect} = useSafeRedirect();
  const [signInConfig, setSignInConfig] = useState(oktaSignInConfig);

  const { authHandler } = useAuthHandler();
  const {logEvent} = useAnalytics();

  const navigate = useNavigate();

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const allowGuest =
    !!params.get("allowGuest") &&
    params.get("allowGuest").toLowerCase() === "true";
  const campaign = params.get("campaign");
  const targetOrigin = params.get("targetOrigin");

  const restoreOriginalUri = async (oktaAuth, originalUri) => {
    const tokens = oktaAuth.tokenManager.getTokensSync();
    const accessToken = {
      token: tokens.accessToken.accessToken,
      expiresAt: tokens.accessToken.expiresAt,
    };
    const idToken = {
      token: tokens.idToken.idToken,
      nonce: tokens.idToken.claims.nonce,
      expiresAt: tokens.idToken.expiresAt,
    };
    const refreshToken = {
      token: tokens.refreshToken.refreshToken,
      expiresAt: tokens.refreshToken.expiresAt,
    };

    // Ajax request to server to set cookies, then redirect
    try {
      let newURI = legacyRedirect(originalUri);

      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken, idToken, refreshToken, newURI }),
      };

      const response = await (await fetch("/api/cookies", options)).json();

      let ots = JSON.parse(localStorage.getItem("okta-token-storage"));
      const removed = "removed...";
      if (!!ots && !!ots.accessToken)
        //some browsers don't do local storage
        ots.accessToken.accessToken = removed;
      if (!!ots && !!ots.refreshToken) ots.refreshToken.refreshToken = removed;
      if (!!ots)
        localStorage.setItem("okta-token-storage", JSON.stringify(ots));

      if (response && response.error) console.log(response);

      if (newURI.startsWith("http")) window.location.replace(newURI);
      else navigate(newURI, { replace: true });
    } catch (err) {
      // how to handle error?
      console.log("error setting cookies", err);
    }
  };

  const getFlags = async () => {
    try {
      const data  = await (await fetch("/api/flagsmith")).json();
      return data;
    }
    catch(err) {
      console.error(err)
      return {};
    }
  }

  React.useEffect(() => {
    const parms = new URLSearchParams(window.location.search);
    const recoveryToken = parms.get("token"); //password recovery
    if (recoveryToken) {
      signInConfig.recoveryToken = recoveryToken;
      setSignInConfig(signInConfig);
    }

    document.cookie =
      "cs_domain=" +
      window.location.origin + 
      ';path=/' +
      ";expires=" +
      new Date(new Date().getTime() + 30 * 60000).toUTCString();
  }, [signInConfig, setSignInConfig]);

  useEffect(() => {
    if (location.pathname && !location.pathname.includes('unlock/') &&
    !location.pathname.includes('activate/') && 
    !location.pathname.includes('recovery/') &&
    !location.pathname.includes('callback')
    ) {
      logEvent(new PageViewEventBase())
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.title])

  let redirectURI = legacyRedirect();

  return (
    <Security
      oktaAuth={oktaAuth}
      onAuthRequired={authHandler}
      restoreOriginalUri={restoreOriginalUri}
    >
      <FlagsmithProvider state={getFlags()}>
      <Suspense fallback={<Loading/>}>
      <Routes>
        <Route
          path="/"
          exact={true}
          element={<Home config={signInConfig} redirect={redirectURI} />}
        />
        <Route
          path="/login"
          element={
            <Login
              allowGuest={allowGuest}
              targetOrigin={targetOrigin}
              campaign={campaign}
              config={signInConfig}
            />
          }
        />
        <Route path="/signin/*" element={<Login config={signInConfig} />} />
        <Route path="/welcome/*" element={<Login config={signInConfig} />} />
        <Route
          path="/\?(?:token|otp|state)="
          element={<Login config={signInConfig} />}
        />
        <Route path="/logout" element={<SecureRoute />}>
          <Route path="" element={<Logout redirect={redirectURI} />} />
        </Route>
        <Route path="/impersonate" element={<SecureRoute />}>
          <Route path="" element={<Impersonate redirect={redirectURI} />} />
        </Route>
        <Route path="/refresh" element={<SecureRoute />}>
          <Route
            path=""
            element={<Refresh config={signInConfig} redirect={redirectURI} />}
          />
        </Route>
        <Route
          path="/sso/auto"
          element={
            <SSOUserRedirect config={signInConfig} redirect={redirectURI} />
          }
        />
        <Route
          path="/sso"
          element={<SSOStaff config={signInConfig} redirect={redirectURI} />}
        />
        <Route path="/account" element={<SecureRoute />}>
          <Route
            path=""
            element={<Account config={signInConfig} redirect={redirectURI} />}
          />
        </Route>
        <Route path="/select-member" element={<SecureRoute />}>
          <Route
            path=""
            element={
              <SelectMember
                campaign={campaign}
                targetOrigin={targetOrigin}
                config={signInConfig}
                redirect={redirectURI}
              />
            }
          />
        </Route>
        <Route
          path="/guest"
          element={<Guest campaign={campaign} targetOrigin={targetOrigin} />}
        />
        <Route path="/auth/callback" element={<LoginCallback />} />
        {!appConfig.isProvider &&
          featureFlags.newMemberAccessAndAuth === "true" && (
            <Route element={<MantineLayout />}>
              <Route path="/account/register" element={<Registration />} />
              <Route
                path="/account/registration-complete"
                element={<RegistrationComplete />}
              />
              <Route path="/account/signin" element={<SignIn />} />
              <Route
                path="/authentication/callback"
                element={<AuthenticationCallback />}
              />
              <Route path="/account/enroll/mfa" element={<MFAEnroll />} />
              <Route
                path="/account/signin/mfa/sms"
                element={<MFAChallenge title='MFA Signin SMS'/>}
              />
              <Route
                path="/account/signin/mfa/email"
                element={<MFAChallenge title='MFA Signin EMAIL'/>}
              />
              <Route
                path="/account/signin/mfa/totp"
                element={<MFAChallengeTotp title='MFA Signin totp'/>}
              />
              <Route
                path="/account/enroll/mfa/sms"
                element={<MFASMSEnroll title='MFA Enroll SMS'/>}
              />
              <Route
                path="/account/enroll/mfa/email"
                element={<MFAEmailEnroll title='MFA Enroll Email'/>}
              />
              <Route path="/account/session" element={<AccountSession />} />
              <Route path="/account/why-verify" element={<WhyConnectPlan />} />
              <Route path="/account/verification" element={<ConnectAccount />} />
              <Route path="/account/verification/relation" element={<ConnectAccountRelation />} />
              <Route
                path="/account/verification/self"
                element={<AccountVerification planType="self" title="self"/>}
              />
              <Route
                path="/account/verification/minor"
                element={<AccountVerification planType="minor" title="minor"/>}
              />
              <Route
                path="/account/verification/guardian"
                element={<AccountVerification planType="guardian" title="guardian"/>}
              />
              <Route
                path="/account/verification/powerofattorney"
                element={<AccountVerification planType="powerofattorney" title="powerofattorney"/>}
              />
              <Route
                path="/account/verification/custodian"
                element={<AccountVerification planType="custodian" title="custodian"/>}
              />
              <Route path="/account/unlocked" element={<AccountUnlocked />} />
              <Route path="/account/emailSent" element={<EmailSent />} />
              <Route path="/account/unlock/:token" element={<ValidateToken tokenType="recovery" isUnlock={true} />} />            
              <Route path="/account/activate/:token" element={<ValidateToken tokenType="activation" />} />
              <Route path="/account/recovery/:token" element={<ValidateToken tokenType="recovery" />} />
              <Route path="/account/set-password" element={<PasswordReset mode="set" />} />
              <Route path="/account/reset-password" element={<PasswordReset mode="reset" />} />
              <Route path="/account/expired-password" element={<PasswordUpdate mode="expired" />} />
              <Route path="/account/forgot-password" element={<ForgotPassword />} />
              <Route path="/account/reset-complete" element={<PasswordResetComplete mode="reset" />} />
              <Route path="/account/set-complete" element={<PasswordResetComplete mode="set" />} />
              <Route path="/account/settings" element={<ValidatedRoute alwaysShowLoginOnFaliure={true} redirect={redirectURI}/>}>
                <Route
                  path=""
                  element={<Account config={signInConfig} redirect={redirectURI} />}
                />
              </Route>
              <Route path="/account/signout" element={<ValidatedRoute alwaysShowLoginOnFaliure={false} redirect={redirectURI}/>}>
                <Route
                  path=""
                  element={<SignOut />}
                />
              </Route>
              <Route path='/request-error' element={<RequestError />} />
            </Route>
          )}
      </Routes>
      </Suspense>
      </FlagsmithProvider>
    </Security>
  );
};
export default AppWithRouterAccess;